import Seo from "@/components/site/Seo";
import { Link } from "react-router-dom";
import Reveal from "@/components/site/Reveal";

const leaders = [
  { name: "Amara Okafor", role: "Ex-Head of Brand · Global fintech", quote: "Great brand work is not a look. It is a system of decisions the whole company can defend.", img: "https://images.unsplash.com/photo-1675726205553-4e348f24da2c?w=900" },
  { name: "Kenji Watanabe", role: "Creative Director · Independent", quote: "Speed without a point of view is just noise dressed up as productivity.", img: "https://images.unsplash.com/photo-1532170579297-281918c8ae72?w=900" },
  { name: "Isabelle Marchetti", role: "Author · Identity systems", quote: "A brand is a promise the market can feel before it can articulate.", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=900" },
  { name: "Rafael Ortiz", role: "Ex-Design Lead · Tier-1 SaaS", quote: "Conversion isn't a color. It's a story told in the right order.", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=900" },
  { name: "Naomi Hart", role: "Founder · Studio in residence", quote: "Design compounds when the same people stay in the room long enough to disagree well.", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=900" },
  { name: "Deven Rao", role: "Design Partner · Growth-stage", quote: "Every rebrand is really an org chart in disguise.", img: "https://images.unsplash.com/photo-1546456073-6712f79251bb?w=900" },
];

export default function Leaders() {
  return (
    <div data-testid="leaders-page">
      <Seo title="Design Leaders | Midcreatives" description="Live working sessions with world-class design leaders — systems taught in the open, real member work reviewed live." path="/leaders" />
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-16 pb-16 md:pb-24 relative">
        <div className="glow-blue" />
        <p className="font-mono-mc mb-6 relative">Leaders · Six sessions a year</p>
        <h1 className="font-display-tight text-5xl sm:text-7xl lg:text-[112px] ink leading-[0.92] max-w-6xl relative">
          The people who set the standard, <span className="ink-muted">in the room with you.</span>
        </h1>
        <p className="ink-muted mt-8 max-w-3xl leading-relaxed text-lg relative">
          Every two months, a design leader takes a live working session with the cohort. They
          teach their system, then review real member work — not slides, not case studies, the
          work in progress on your screens right now.
        </p>
      </section>

      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {leaders.map((l, i) => (
            <Reveal key={l.name} delay={i * 0.05}>
              <div className="card-white rounded-3xl overflow-hidden group hover:border-white/25 transition-all duration-500">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={l.img} alt={l.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000" />
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="font-display-tight text-3xl ink">{l.name}</h3>
                  <p className="font-mono-mc mt-1">{l.role}</p>
                  <p className="ink text-lg leading-relaxed mt-4">"{l.quote}"</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-8 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono-mc mb-4">Format</p>
            <h2 className="font-display-tight text-4xl md:text-5xl ink leading-[1.0]">
              Teach the system. <span className="text-lime">Review the actual work.</span>
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { t: "Live sessions", d: "Six per year. Two hours each. Cameras on. Working, not watching." },
              { t: "Masterclasses", d: "Two full-day deep dives. Brand at scale. Conversion-driven design." },
              { t: "Office hours", d: "One-on-one time with visiting leaders at the annual summit." },
              { t: "Library", d: "Every session archived for members. Frameworks compound year over year." },
            ].map((c, i) => (
              <div key={c.t} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7">
                <p className="font-mono-mc">{String(i + 1).padStart(2, "0")}</p>
                <h4 className="font-display-tight text-2xl ink mt-4">{c.t}</h4>
                <p className="ink-muted mt-3 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-end justify-between gap-8 relative overflow-hidden">
          <div className="glow-lime" />
          <h2 className="font-display-tight text-4xl md:text-6xl ink max-w-3xl leading-[1.0] relative">
            Learn from the people your team already <span className="text-lime">quotes.</span>
          </h2>
          <Link to="/apply" data-testid="leaders-apply-cta" className="btn-lime relative">
            Get Membership <span className="arrow-slide">→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
